import React, { useState, useEffect } from 'react'
import useAxios from '@use-hooks/axios'
import {
  FiltersType,
  ActiveFilters,
  ActiveDimensions,
  FilterCategoryType,
  SortEnum,
} from '../types'
import { capitalizeFirstLetter } from '../utils'

interface FiltersResponse {
  onSale: boolean
  filters: FiltersType
}

export interface FiltersContext {
  activeFilters: ActiveFilters
  setActiveFilters: (filters: ActiveFilters) => void
  activeDimensions: ActiveDimensions
  setActiveDimensions: (dimensions: ActiveDimensions) => void
  filters: FiltersType
  filtersLoading: boolean
  filtersError?: Error
  onSale: boolean
  title: string
  setTitle: (title: string) => void
  activeCategories: Set<FilterCategoryType>
  setActiveCategories: (categories: Set<FilterCategoryType>) => void
  toggleCategory: (filterType: FilterCategoryType) => void
  toggleFilter: (filterType: string, filter: string) => void
  clearActiveFilters: () => void
  page: number
  setPage: (page: number) => void
  sort: SortEnum
  setSort: (sort: SortEnum) => void
  clearance: boolean
  setClearance: (clearance: boolean) => void
}

const FiltersContext = React.createContext<FiltersContext | null>(null)

const DEFAULT_FILTERS: ActiveFilters = {
  range: new Set(),
  seatType: new Set(),
  colour: new Set(),
}

const DEFAULT_DIMENSIONS: ActiveDimensions = {
  height: null,
  width: null,
  depth: null,
}

export const FiltersProvider: React.FC = ({ children }) => {
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(
    DEFAULT_FILTERS
  )
  const [activeDimensions, setActiveDimensions] = useState<ActiveDimensions>(
    DEFAULT_DIMENSIONS
  )

  const [data, setData] = useState<FiltersResponse>();
  const [sort, setSort] = useState<SortEnum>(SortEnum.SeatType)
  const [page, setPage] = useState(1)
  const [title, setTitle] = useState('Leather Sofas & Armchairs')
  const [activeCategories, setActiveCategories] = useState<
    Set<FilterCategoryType>
  >(new Set(['seatType', 'range']))
  const [clearance, setClearance] = useState(false)

  const { error, loading, response } = useAxios<string>({
    url: `/api/getfilters`,
    method: 'GET',
    trigger: 'true',
    customHandler: (_, response) => {
      if (response && response.data) {
        const data = JSON.parse(response.data)
        setData(data);
      }
    }
  })

  // NB: on mount, get url filters
  useEffect(() => {
    setUrlFilters()
  }, [])

  // NB: when the sort or active filters change, reset page to 1
  useEffect(() => {
    setPage(1)
    window.scrollTo(0, 0)
  }, [sort, activeFilters, activeDimensions, clearance])

  // NB: when active filters change, animate products list
  useEffect(() => {
    window.dispatchEvent(new Event('resize'))
    // TODO: move this into a React animated transition block
    const productList = document.querySelectorAll('.productlist-products')
    if (!productList || !productList[0]) return

    productList[0].className = 'productlist-products __fadeout'
    setTimeout(() => {
      productList[0].className = 'productlist-products'
    }, 500)
  }, [activeFilters, activeDimensions, clearance])

  const setUrlFilters = () => {
    // get url and set filters as active filters
    const urlPaths = window.location.pathname.split('/')

    // if only range is set
    if (urlPaths.length == 4) {
      if (urlPaths[2] === "clearance") {
        setClearance(true);
      } else {
        setRangeFromURL(urlPaths[2])
      }
    }

    //if range and seat type is set
    if (urlPaths.length == 5) {
      setRangeFromURL(urlPaths[2])
      setSeatTypeFromURL(urlPaths[3])
    }

    //if range, seat type and colour is set
    if (urlPaths.length == 6) {
      setRangeFromURL(urlPaths[2])
      setSeatTypeFromURL(urlPaths[3])
      setColourFromURL(urlPaths[4])
    }
  }

  const setSeatTypeFromURL = (type: string) => {
    if (type === 'all') return

    if (type === 'chairs') {
      setActiveFilters({
        ...activeFilters,
        seatType: new Set(['Club Chair', 'Recliner Chair', 'Highback Chair']),
      })
    } else {
      const urlSeatTypeSplit = type.split('-')
      for (let i = 0; i < urlSeatTypeSplit.length; i++) {
        urlSeatTypeSplit[i] = capitalizeFirstLetter(urlSeatTypeSplit[i])
      }
      const urlSeatTypeJoined = urlSeatTypeSplit.join(' ')
      setActiveFilters({
        ...activeFilters,
        seatType: new Set([urlSeatTypeJoined]),
      })
    }
  }

  const setRangeFromURL = (range: string) => {
    if (range === 'all') return

    const urlRangeCap = range
      .split('-')
      .map(word => capitalizeFirstLetter(word))
      .join(' ')

    setActiveFilters({
      ...activeFilters,
      range: new Set([urlRangeCap]),
    })
  }

  const setColourFromURL = (colour: string): void => {
    const urlColour = colour
      .split('-')
      .map(s => capitalizeFirstLetter(s))
      .join(' ')

    setActiveFilters({
      ...activeFilters,
      colour: new Set([urlColour]),
    })
  }

  const toggleCategory = (category: FilterCategoryType): void => {
    const categories = new Set(activeCategories)

    if (categories.has(category)) {
      categories.delete(category)
    } else {
      categories.add(category)
    }

    setActiveCategories(categories)
  }

  const clearActiveFilters = (): void => {
    setActiveFilters({ ...DEFAULT_FILTERS })
    setActiveDimensions({ ...DEFAULT_DIMENSIONS })
  }

  const toggleFilter = (category: FilterCategoryType, filter: string): void => {
    //Get all active filters and deal with checkboxes
    const currentFilters = activeFilters[category]

    if (currentFilters.has(filter)) {
      currentFilters.delete(filter)
    } else {
      currentFilters.add(filter)
    }

    // Update active filters
    setActiveFilters({
      ...activeFilters,
      currentFilters,
    })
  }

  if (!data) return null

  return (
    <FiltersContext.Provider
      value={{
        activeFilters,
        filters: data.filters,
        setActiveFilters,
        activeDimensions,
        setActiveDimensions,
        filtersLoading: loading,
        filtersError: error,
        onSale: data.onSale,
        title,
        setTitle,
        activeCategories,
        setActiveCategories,
        toggleFilter,
        toggleCategory,
        clearActiveFilters,
        page,
        setPage,
        sort,
        setSort,
        clearance,
        setClearance,
      }}
    >
      {children}
    </FiltersContext.Provider>
  )
}

export const useFilters = (): FiltersContext | null =>
  React.useContext(FiltersContext)
